import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { takeUntil, finalize } from 'rxjs/operators';
import { IDashboardWidget } from 'src/app/models/dashboard';
import { SegProfileService } from 'src/app/services/seg-profile.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { ValueConversionService } from 'src/app/services/value-conversion.service';
import { WidgetOrgFilterComponent } from 'src/app/common-components/widget-org-filter/widget-org-filter.component';
import { DateFilterTypeEnum } from 'src/app/enums/dateFilterType.enum';
import { MethodOfAssessment } from 'src/app/enums/methodOfAssessment.enum';
import { Units, UnitSymbols } from 'src/app/enums/units.enum';
import { SegProfileSummaryResponse, SegProfileRowResponse, SegGroupPlanResponse } from 'src/app/models/seg-profile-summary.model';
import { resolveCoverage, coverageRatio, CoverageBadge } from 'src/app/shared/utils/sampling-coverage.util';
import { BaseDashboardWidget } from 'src/app/components/dashboard/dashboard-widgets/_shared/base-dashboard-widget';
import { WidgetTableSort } from '../_shared/widget-table-sort';
import { SegProfileSummaryFiltersComponent } from './seg-profile-summary-filters.component';
import {
    SegProfileFilterValues,
    SegProfileSortColumn,
    SEG_PROFILE_FILTER_DEFAULTS,
    SEG_PROFILE_TABLE_HEADERS,
    configToFilterValues,
    filterValuesToConfig,
} from './seg-profile-summary-widget.constants';
import { resolveDateFilterRange } from '../_shared/date-filter.util';
import { RoutePaths } from 'src/app/shared/constants/routePaths.constants';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { SharedModule } from 'src/app/shared/shared.module';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { resolveDistributionLabelKey } from 'src/app/shared/utils/distribution-label.util';

export interface SegProfileDisplayRow extends SegProfileRowResponse {
    trendIcon: string;
    trendClass: string;
    typeLabel: string;
    isQuantitative: boolean;
    isQualitative: boolean;
    distributionLabel: string;
    convertedDsvDisplay: string;
    coverageRatio: number | null;
    coverageBadge: CoverageBadge;
}

export interface SegGroupPlanDisplayRow extends SegGroupPlanResponse {
    coverageBadge: CoverageBadge;
}

@Component({
    standalone: true,
    selector: 'app-seg-profile-summary-widget',
    imports: [
        CommonModule,
        RouterModule,
        TranslateModule,
        MatIconModule,
        MatTooltipModule,
        SegProfileSummaryFiltersComponent,
        WidgetOrgFilterComponent,
        SharedModule,
    ],
    templateUrl: './seg-profile-summary-widget.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./seg-profile-summary-widget.component.scss']
})
export class SegProfileSummaryWidgetComponent
    extends BaseDashboardWidget
    implements IDashboardWidget
{
    summary: SegProfileSummaryResponse | null = null;
    displayRows: SegProfileDisplayRow[] = [];
    groupPlanRows: SegGroupPlanDisplayRow[] = [];
    selectedRow: SegProfileDisplayRow | null = null;
    readonly samplingPlansRoute = RoutePaths.samplingPlans;
    readonly segSamplingPlanPermission = RolePermissionObjects.SegSamplingPlan;
    readonly sort = new WidgetTableSort<SegProfileDisplayRow, SegProfileSortColumn>(
        (row, column) => row[column],
        SEG_PROFILE_FILTER_DEFAULTS.sortColumn,
        SEG_PROFILE_FILTER_DEFAULTS.sortAsc,
    );

    tempFilterValues: SegProfileFilterValues = { ...SEG_PROFILE_FILTER_DEFAULTS };

    private requestVersion = 0;

    readonly riskAssessmentsRoute = RoutePaths.riskAssessments;
    readonly tableHeaders = SEG_PROFILE_TABLE_HEADERS;
    readonly trackByIndex = trackByIndex;

    readonly trackBySegAgent = (_: number, row: SegProfileDisplayRow): string => `${row.segId}_${row.agentId}`;

    readonly trackByGroupPlan = (_: number, row: SegGroupPlanDisplayRow): string => `${row.segId}_${row.agentGroupId}`;

    constructor(
        private readonly segProfileService: SegProfileService,
        private readonly translate: TranslateService,
        private readonly dateFormatService: DateFormatService,
        private readonly valueConversionService: ValueConversionService,
    ) {
        super();
    }

    onConfigChange?(config: Record<string, unknown>): void {
        this.widgetConfig = config;
        this.syncFromConfig();
        this.reload();
    }

    onFilterChange(values: SegProfileFilterValues): void {
        this.tempFilterValues = values;
        this.reload();
    }

    sortBy(col: SegProfileSortColumn): void {
        this.sort.toggle(col);
        this.persistSort();
        this.buildDisplayRows();
    }

    selectRow(row: SegProfileDisplayRow): void {
        this.selectedRow = this.selectedRow?.segId === row.segId && this.selectedRow?.agentId === row.agentId
            ? null
            : row;
    }

    protected override syncFromConfig(): void {
        this.tempFilterValues = configToFilterValues(this.widgetConfig);
        this.sort.column = this.tempFilterValues.sortColumn;
        this.sort.ascending = this.tempFilterValues.sortAsc;
    }

    private persistSort(): void {
        this.tempFilterValues = { ...this.tempFilterValues, sortColumn: this.sort.column, sortAsc: this.sort.ascending };
        this.widgetConfig = { ...this.widgetConfig, ...filterValuesToConfig(this.tempFilterValues) };
        this.configChange.emit(this.widgetConfig);
    }

    protected override reload(): void {
        this.loadSummary();
    }

    private buildDisplayRows(): void {
        if (!this.summary) {
            this.displayRows = [];
            return;
        }
        const fullYearWindow = this.isFullYearWindow();
        const rows: SegProfileDisplayRow[] = this.summary.rows.map(r => {
            const isQualitative = r.methodOfAssessment === MethodOfAssessment.Qualitative;
            return {
                ...r,
                trendIcon: this.resolveTrendIcon(r.trendDirection),
                trendClass: this.resolveTrendClass(r.trendDirection),
                typeLabel: this.resolveTypeLabel(r.methodOfAssessment),
                isQuantitative: r.methodOfAssessment === MethodOfAssessment.Quantitative,
                isQualitative,
                distributionLabel: isQualitative
                    ? ''
                    : this.resolveDistributionLabel(r.isNormal, r.isLogNormal),
                convertedDsvDisplay: isQualitative
                    ? ''
                    : this.resolveConvertedDsvDisplay(r.decisionStatisticValue, r.agentUnits),
                coverageRatio: isQualitative ? null : coverageRatio(r.plannedNumberOfSamples, r.liveSampleCount),
                coverageBadge: resolveCoverage(r.plannedNumberOfSamples, r.liveSampleCount, this.translate, fullYearWindow),
            };
        });
        this.displayRows = this.sort.apply(rows);
    }

    private buildGroupPlanRows(): void {
        if (!this.summary?.groupPlans?.length) {
            this.groupPlanRows = [];
            return;
        }
        const fullYearWindow = this.isFullYearWindow();
        this.groupPlanRows = this.summary.groupPlans.map(g => ({
            ...g,
            coverageBadge: resolveCoverage(g.plannedNumberOfSamples, g.actualCount, this.translate, fullYearWindow),
        }));
    }

    private isFullYearWindow(): boolean {
        const ft = this.tempFilterValues.dateFilterType;
        return ft == null
            || ft === DateFilterTypeEnum.AllDates
            || ft === DateFilterTypeEnum.ThisYear
            || ft === DateFilterTypeEnum.LastYear;
    }

    private resolveTypeLabel(methodOfAssessment: MethodOfAssessment): string {
        if (methodOfAssessment === MethodOfAssessment.Quantitative) return this.translate.instant('Quantitative');
        if (methodOfAssessment === MethodOfAssessment.Qualitative) return this.translate.instant('Qualitative');
        return '–';
    }

    private resolveTrendIcon(direction: number | null): string {
        if (direction == null) return 'remove';
        if (direction > 0) return 'trending_up';
        if (direction < 0) return 'trending_down';
        return 'trending_flat';
    }

    private resolveTrendClass(direction: number | null): string {
        if (direction == null) return '';
        if (direction > 0) return 'trend-worse';
        if (direction < 0) return 'trend-better';
        return 'trend-flat';
    }

    private resolveDistributionLabel(isNormal: boolean | null, isLogNormal: boolean | null): string {
        return this.translate.instant(resolveDistributionLabelKey(isNormal, isLogNormal));
    }

    private resolveConvertedDsvDisplay(value: number | null, unit: Units | null): string {
        if (value == null) return '';
        const converted = unit != null
            ? this.valueConversionService.getParameterValueAsPerAgentUnit(value, unit)
            : value;
        const symbol = unit != null ? UnitSymbols[unit] : '';
        return `${+converted.toFixed(4)}${symbol ? ' ' + symbol : ''}`;
    }

    private buildWindowTimestamps(): { windowStart: number | null; windowEnd: number | null } {
        const ft = this.tempFilterValues.dateFilterType;
        if (ft == null || ft === DateFilterTypeEnum.AllDates) {
            return { windowStart: null, windowEnd: null };
        }
        if (ft === DateFilterTypeEnum.Custom) {
            const s = this.tempFilterValues.customStartDate
                ? this.dateFormatService.convertDateToTimestamp(new Date(this.tempFilterValues.customStartDate))
                : null;
            const e = this.tempFilterValues.customEndDate
                ? this.dateFormatService.convertDateToTimestamp(new Date(this.tempFilterValues.customEndDate))
                : null;
            return { windowStart: s, windowEnd: e };
        }
        const range = resolveDateFilterRange(ft);
        if (!range) return { windowStart: null, windowEnd: null };
        return {
            windowStart: this.dateFormatService.convertDateToTimestamp(range.start),
            windowEnd: this.dateFormatService.convertDateToTimestamp(range.end),
        };
    }

    private loadSummary(): void {
        const currentVersion = ++this.requestVersion;
        const effectiveOrgId = this.resolveEffectiveOrgIdAndRecord();
        if (!effectiveOrgId) {
            this.summary = null;
            this.displayRows = [];
            this.groupPlanRows = [];
            return;
        }

        this.isLoading = true;
        this.error = undefined;

        const { windowStart, windowEnd } = this.buildWindowTimestamps();

        this.segProfileService.getSegProfileSummary({
            organizationLevelId: effectiveOrgId,
            agentId: this.tempFilterValues.agentId,
            surveyTypeIds: this.tempFilterValues.surveyTypeIds.length ? this.tempFilterValues.surveyTypeIds : undefined,
            windowStart,
            windowEnd,
        }).pipe(
            takeUntil(this.destroy$),
            finalize(() => {
                if (currentVersion === this.requestVersion) {
                    this.isLoading = false;
                }
            }),
        ).subscribe({
            next: (response) => {
                if (currentVersion !== this.requestVersion) return;
                this.summary = response;
                this.buildDisplayRows();
                this.buildGroupPlanRows();
                if (
                    this.selectedRow &&
                    !this.displayRows.some(r => r.segId === this.selectedRow!.segId && r.agentId === this.selectedRow!.agentId)
                ) {
                    this.selectedRow = null;
                }
            },
            error: () => {
                if (currentVersion !== this.requestVersion) return;
                this.error = this.translate.instant('Failed to load SEG profile data');
                this.summary = null;
                this.displayRows = [];
                this.groupPlanRows = [];
            },
        });
    }
}
